<template>
                <div v-if="currentView === 'rag_chat'">
                    <div class="page-header">
                        <div>
                            <div class="page-title">企業知識庫</div>
                            <div class="page-subtitle">AI 問答、企業文件管理、郵件同步（RAG）</div>
                        </div>
                    </div>

                    <!-- Tabs -->
                    <div class="flex gap-1 mb-4 border-b" style="border-color:var(--border);">
                        <button
                            v-for="tab in ragTabs" :key="tab.key"
                            class="px-4 py-2 text-sm font-medium rounded-t transition-colors"
                            :style="ragState.activeTab === tab.key
                                ? 'background:var(--surface);border:1px solid var(--border);border-bottom:1px solid var(--surface);color:var(--primary);margin-bottom:-1px;'
                                : 'color:var(--gray-500);'"
                            @click="ragState.activeTab = tab.key">
                            {{ tab.label }}
                        </button>
                    </div>

                    <!-- Tab: AI 問答 -->
                    <div v-if="ragState.activeTab === 'chat'" class="grid grid-cols-1 md:grid-cols-3 gap-6" style="height:calc(100vh - 14rem);">
                        <div class="col-span-2 rounded shadow flex flex-col h-full" style="background:var(--surface);">
                            <div class="flex-1 p-4 overflow-y-auto space-y-4" id="chat-messages">
                                <div v-for="(msg, index) in chatMessages" :key="index"
                                    :class="msg.role === 'user' ? 'text-right' : 'text-left'">
                                    <div class="inline-block p-3 rounded-lg max-w-[80%] whitespace-pre-wrap text-sm"
                                        :style="msg.role === 'user'
                                            ? 'background:var(--primary-light,#dbeafe);color:var(--primary-dark,#1e40af);margin-left:auto;'
                                            : 'background:var(--gray-100);color:var(--gray-800);margin-right:auto;'">
                                        <span class="font-bold block text-xs mb-1">{{ msg.role === 'user' ? 'You' : 'AI Assistant' }}</span>
                                        {{ msg.content }}
                                        <div v-if="msg.sources && msg.sources.length" class="mt-2 pt-2 border-t text-xs" style="border-color:var(--border);color:var(--gray-500);">
                                            <span class="font-semibold">來源：</span>
                                            <span v-for="(s, si) in msg.sources" :key="si" class="inline-block mr-2">{{ s.source_name || s.doc_id }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="isChatLoading" class="text-left">
                                    <div class="inline-block p-3 rounded-lg" style="background:var(--gray-100);color:var(--gray-500);">思考中...</div>
                                </div>
                            </div>
                            <div class="p-4 border-t" style="border-color:var(--border);">
                                <form @submit.prevent="sendRagMessage" class="flex gap-2">
                                    <input v-model="chatInput" type="text" placeholder="詢問關於您郵件或知識庫的問題..."
                                        class="flex-1 border p-2 rounded text-sm focus:outline-none"
                                        style="background:var(--surface);border-color:var(--border);color:var(--text);"
                                        :disabled="isChatLoading">
                                    <button type="submit" class="btn btn-primary" :disabled="isChatLoading">發送</button>
                                </form>
                            </div>
                        </div>
                        <!-- Stats panel -->
                        <div class="card p-4 h-full overflow-y-auto flex flex-col gap-4">
                            <div>
                                <div class="text-sm font-semibold mb-1" style="color:var(--gray-700);">知識庫統計</div>
                                <div class="text-xs" style="color:var(--gray-500);">
                                    <div>郵件向量：{{ ragStats?.email_count ?? '—' }}</div>
                                    <div>知識文件：{{ ragStats?.knowledge_count ?? '—' }}</div>
                                </div>
                                <button class="btn btn-secondary mt-2 text-xs" @click="fetchRagStats">重新整理</button>
                            </div>
                            <div>
                                <div class="text-sm font-semibold mb-1" style="color:var(--gray-700);">KB 查詢測試</div>
                                <input v-model="kbAdminQueryInput" type="text" placeholder="查詢知識庫..."
                                    class="w-full border p-2 rounded text-sm mb-2"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                <button class="btn btn-primary w-full text-sm" @click="queryRag(kbAdminQueryInput)" :disabled="kbAdminLoading">
                                    {{ kbAdminLoading ? '查詢中...' : '查詢' }}
                                </button>
                                <div v-if="kbAdminError" class="text-xs mt-1" style="color:var(--danger);">{{ kbAdminError }}</div>
                                <div v-if="kbAdminResult" class="text-xs mt-2 p-2 rounded" style="background:var(--gray-100);color:var(--gray-800);">{{ kbAdminResult }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Tab: 企業文件管理 -->
                    <div v-if="ragState.activeTab === 'docs'">
                        <!-- Upload area -->
                        <div class="card p-4 mb-4">
                            <div class="flex flex-wrap items-center gap-3 mb-3">
                                <div class="text-sm font-semibold" style="color:var(--gray-700);">上傳企業文件</div>
                                <div class="flex flex-wrap items-center gap-2 ml-auto">
                                    <div class="flex items-center gap-1">
                                        <span class="text-xs" style="color:var(--gray-500);">資料夾：</span>
                                        <select v-model="uploadFolderId" class="border rounded px-2 py-1 text-xs"
                                            style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                            <option :value="null">📁 根目錄</option>
                                            <option v-for="f in folderState.folders" :key="f.id" :value="f.id">📂 {{ f.name }}</option>
                                        </select>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <span class="text-xs" style="color:var(--gray-500);">狀態：</span>
                                        <select v-model="uploadOptions.status" class="border rounded px-2 py-1 text-xs"
                                            style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                            <option value="published">已發佈</option>
                                            <option value="draft">草稿</option>
                                        </select>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <span class="text-xs" style="color:var(--gray-500);">到期：</span>
                                        <input type="date" v-model="uploadOptions.expiresAt"
                                            class="border rounded px-2 py-1 text-xs"
                                            style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                    </div>
                                </div>
                            </div>
                            <div
                                class="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
                                :style="isDragOver
                                    ? 'border-color:var(--primary);background:var(--primary-light,#dbeafe);'
                                    : 'border-color:var(--border);background:var(--gray-50,#f9fafb);'"
                                @dragover.prevent="isDragOver = true"
                                @dragleave="isDragOver = false"
                                @drop.prevent="handleFileDrop($event)"
                                @click="$refs.fileInput.click()">
                                <input ref="fileInput" type="file" class="hidden" accept=".pdf,.txt,.docx,.md" @change="handleFileSelect($event)" multiple>
                                <div style="font-size:1.6rem;">📄</div>
                                <div class="text-sm font-medium mt-1" style="color:var(--gray-700);">
                                    {{ uploadOptions.updateKbId ? '更新現有文件版本（拖曳或點擊）' : '拖曳或點擊上傳新文件' }}
                                </div>
                                <div class="text-xs mt-0.5" style="color:var(--gray-400);">支援 PDF、TXT、DOCX、MD（最大 10MB）</div>
                                <div v-if="uploadOptions.updateKbId" class="mt-2">
                                    <span class="text-xs px-2 py-1 rounded" style="background:#fef3c7;color:#92400e;">
                                        更新模式：{{ (ragState.knowledgeDocs||[]).find(d=>d.kb_id===uploadOptions.updateKbId)?.source_name || '文件 #'+uploadOptions.updateKbId }}
                                    </span>
                                    <button class="text-xs ml-2" style="color:var(--danger);" @click.stop="uploadOptions.updateKbId=null">取消更新</button>
                                </div>
                            </div>
                            <div v-if="ragState.uploadLoading" class="flex items-center gap-2 mt-3 text-sm" style="color:var(--primary);">
                                <span class="animate-spin">⟳</span> 上傳中，AI 自動摘要生成中，請稍候...
                            </div>
                            <div v-if="ragState.uploadError" class="mt-2 text-sm" style="color:var(--danger);">{{ ragState.uploadError }}</div>
                        </div>

                        <!-- Folder + Document layout -->
                        <div class="flex gap-4">
                            <!-- Folder sidebar -->
                            <div class="flex-shrink-0 rounded-lg border overflow-hidden" style="width:200px;background:var(--surface);border-color:var(--border);">
                                <div class="px-3 py-2 border-b text-xs font-semibold" style="border-color:var(--border);color:var(--gray-600);background:var(--gray-50);">
                                    資料夾
                                </div>
                                <!-- 全部 -->
                                <button
                                    class="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                                    :style="folderState.currentId === null
                                        ? 'background:var(--primary-light,#dbeafe);color:var(--primary);font-weight:600;'
                                        : 'color:var(--text);'"
                                    @click="folderState.currentId = null">
                                    📁 全部文件
                                    <span class="ml-auto text-xs" style="color:var(--gray-400);">{{ ragState.knowledgeDocs?.length || 0 }}</span>
                                </button>
                                <!-- 各資料夾 -->
                                <div v-if="folderState.loading" class="px-3 py-2 text-xs" style="color:var(--gray-400);">載入中...</div>
                                <div v-for="f in folderState.folders" :key="f.id" class="relative group">
                                    <button
                                        class="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                                        :style="folderState.currentId === f.id && tagFilterId === null
                                            ? 'background:var(--primary-light,#dbeafe);color:var(--primary);font-weight:600;'
                                            : 'color:var(--text);'"
                                        @click="folderState.currentId = f.id; tagFilterId = null">
                                        📂 <span class="flex-1 truncate">{{ f.name }}</span>
                                        <span class="text-xs" style="color:var(--gray-400);">
                                            {{ (ragState.knowledgeDocs || []).filter(d => d.folder_id === f.id).length }}
                                        </span>
                                    </button>
                                    <button
                                        class="absolute right-1 top-1/2 -translate-y-1/2 text-xs px-1 hidden group-hover:block"
                                        style="color:var(--danger);"
                                        title="刪除資料夾"
                                        @click.stop="confirmDeleteFolder(f)">✕</button>
                                </div>
                                <div class="border-t px-3 py-2" style="border-color:var(--border);">
                                    <button class="btn btn-secondary text-xs w-full" @click="newFolderModal.visible = true">
                                        + 新增資料夾
                                    </button>
                                </div>
                                <!-- 標籤區 -->
                                <div class="border-t pt-2" style="border-color:var(--border);">
                                    <div class="px-3 py-1 text-xs font-semibold" style="color:var(--gray-500);">🏷 標籤</div>
                                    <div v-if="!tagState.tags.length" class="px-3 py-1 text-xs" style="color:var(--gray-400);">尚無標籤</div>
                                    <div v-for="tag in tagState.tags" :key="tag.id" class="relative group flex items-center">
                                        <button
                                            class="flex-1 text-left px-3 py-1.5 text-xs flex items-center gap-2"
                                            :style="tagFilterId === tag.id
                                                ? 'background:var(--primary-light,#dbeafe);color:var(--primary);font-weight:600;'
                                                : 'color:var(--text);'"
                                            @click="tagFilterId = (tagFilterId === tag.id ? null : tag.id); folderState.currentId = null">
                                            <span class="inline-block w-2 h-2 rounded-full flex-shrink-0" :style="`background:${tag.color}`"></span>
                                            <span class="flex-1 truncate">{{ tag.name }}</span>
                                            <span class="text-xs" style="color:var(--gray-400);">
                                                {{ (ragState.knowledgeDocs || []).filter(d => (d.tags||[]).some(t=>t.id===tag.id)).length }}
                                            </span>
                                        </button>
                                        <button class="hidden group-hover:block text-xs pr-2" style="color:var(--danger);"
                                            @click.stop="confirmDeleteTag(tag)">✕</button>
                                    </div>
                                    <div class="px-3 py-2">
                                        <button class="btn btn-secondary text-xs w-full" @click="newTagModal.visible = true">
                                            + 新增標籤
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Document list -->
                            <div class="flex-1 card p-0 overflow-hidden min-w-0">
                                <!-- Toolbar: search + batch -->
                                <div class="flex flex-wrap items-center gap-2 px-4 py-3 border-b" style="border-color:var(--border);">
                                    <input v-model="searchQ"
                                        @input="onSearchInput"
                                        class="border rounded px-3 py-1.5 text-sm flex-1 min-w-0"
                                        style="background:var(--surface);border-color:var(--border);color:var(--text);max-width:260px;"
                                        placeholder="🔍 搜尋文件名稱...">
                                    <div class="text-sm font-semibold flex-1" style="color:var(--gray-700);">
                                        <span v-if="tagFilterId !== null">
                                            🏷 {{ tagState.tags.find(t=>t.id===tagFilterId)?.name }}（{{ displayedDocs.length }}）
                                        </span>
                                        <span v-else-if="folderState.currentId !== null">
                                            📂 {{ folderState.folders.find(f=>f.id===folderState.currentId)?.name }}（{{ displayedDocs.length }}）
                                        </span>
                                        <span v-else>全部文件（{{ displayedDocs.length }}）</span>
                                    </div>
                                    <button class="btn text-xs flex-shrink-0"
                                        :style="showFavoritesOnly ? 'color:#f59e0b;font-weight:600;' : 'color:var(--gray-400);'"
                                        @click="showFavoritesOnly = !showFavoritesOnly"
                                        :title="showFavoritesOnly ? '顯示全部' : '只看收藏'">⭐ 收藏</button>
                                    <!-- Batch actions (visible when any selected) -->
                                    <div v-if="selectedDocIds.size > 0" class="flex items-center gap-1 flex-shrink-0">
                                        <span class="text-xs font-medium px-2" style="color:var(--primary);">已選 {{ selectedDocIds.size }}</span>
                                        <button class="btn btn-secondary text-xs" @click="openBatchMoveModal">📂 移動</button>
                                        <button class="btn btn-danger text-xs" @click="doBatchDelete">刪除</button>
                                        <button class="btn btn-secondary text-xs" @click="selectedDocIds = new Set()">取消</button>
                                    </div>
                                    <button class="btn btn-secondary text-xs flex-shrink-0" @click="exportKbZip()" title="匯出知識庫 ZIP">📦 匯出</button>
                                    <button class="btn btn-secondary text-xs flex-shrink-0" @click="fetchRagDocuments(searchQ.trim()||undefined)" :disabled="ragState.knowledgeDocsLoading">
                                        <span v-if="ragState.knowledgeDocsLoading" class="animate-spin">⟳</span>
                                        {{ ragState.knowledgeDocsLoading ? '...' : '↻' }}
                                    </button>
                                </div>
                                <div v-if="ragState.knowledgeDocsLoading && !ragState.knowledgeDocs.length" class="p-8 text-center text-sm" style="color:var(--gray-400);">載入中...</div>
                                <div v-else-if="!displayedDocs.length" class="p-8 text-center text-sm" style="color:var(--gray-400);">
                                    {{ searchQ ? '沒有符合的文件' : folderState.currentId !== null ? '此資料夾尚無文件' : tagFilterId !== null ? '此標籤尚無文件' : '尚無文件。請上傳企業相關文件以建立知識庫。' }}
                                </div>
                                <div v-else class="overflow-x-auto">
                                    <table class="w-full text-sm">
                                        <thead>
                                            <tr style="background:var(--gray-50);border-bottom:1px solid var(--border);">
                                                <th class="px-3 py-2 w-8">
                                                    <input type="checkbox" :checked="allDisplayedSelected" @change="toggleSelectAll">
                                                </th>
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">文件名稱</th>
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">狀態</th>
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">標籤</th>
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">分享</th>
                                                <th class="text-left px-3 py-2 font-medium hidden xl:table-cell" style="color:var(--gray-600);">AI 摘要</th>
                                                <th class="text-right px-3 py-2 font-medium" style="color:var(--gray-600);">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="doc in displayedDocs" :key="doc.id"
                                                class="border-b transition-colors"
                                                :style="selectedDocIds.has(doc.id)
                                                    ? 'border-color:var(--border);background:var(--primary-light,#dbeafe);'
                                                    : 'border-color:var(--border);'">
                                                <td class="px-3 py-3">
                                                    <input type="checkbox"
                                                        :checked="selectedDocIds.has(doc.id)"
                                                        @change="toggleSelectDoc(doc.id)">
                                                </td>
                                                <!-- 文件名稱 + 版本 + 到期 -->
                                                <td class="px-3 py-3 font-medium" style="color:var(--text);">
                                                    <div class="flex items-center gap-1">
                                                        <div class="truncate" style="max-width:12rem;" :title="doc.source_name || doc.id">
                                                            {{ doc.source_name || doc.id }}
                                                        </div>
                                                        <span v-if="doc.version > 1"
                                                            class="text-xs px-1 rounded flex-shrink-0"
                                                            style="background:#e0e7ff;color:#4338ca;">v{{ doc.version }}</span>
                                                    </div>
                                                    <div v-if="folderState.currentId === null && doc.folder_id" class="text-xs mt-0.5" style="color:var(--gray-400);">
                                                        📂 {{ folderState.folders.find(f=>f.id===doc.folder_id)?.name }}
                                                    </div>
                                                    <!-- 到期日提示 -->
                                                    <div v-if="doc.expires_at && isExpired(doc.expires_at)"
                                                        class="text-xs mt-0.5 font-semibold" style="color:#dc2626;">
                                                        ⚠ 已到期 {{ doc.expires_at?.slice(0,10) }}
                                                    </div>
                                                    <div v-else-if="doc.expires_at && isExpiringSoon(doc.expires_at)"
                                                        class="text-xs mt-0.5" style="color:#d97706;">
                                                        ⏰ 即將到期 {{ doc.expires_at?.slice(0,10) }}
                                                    </div>
                                                    <div v-else-if="doc.expires_at"
                                                        class="text-xs mt-0.5" style="color:var(--gray-400);">
                                                        📅 到期 {{ doc.expires_at?.slice(0,10) }}
                                                    </div>
                                                </td>

                                                <!-- 狀態 -->
                                                <td class="px-3 py-3">
                                                    <div class="flex flex-col gap-1 items-start">
                                                        <span class="text-xs px-2 py-0.5 rounded-full font-semibold"
                                                            :style="STATUS_STYLE[doc.status] || STATUS_STYLE.draft">
                                                            {{ STATUS_LABEL[doc.status] || doc.status }}
                                                        </span>
                                                        <select v-if="doc.is_owner !== false"
                                                            class="text-xs border rounded px-1 py-0.5 mt-0.5"
                                                            style="background:var(--surface);border-color:var(--border);color:var(--gray-500);"
                                                            :value="doc.status"
                                                            @change="changeDocStatus(doc, $event.target.value)">
                                                            <option value="published">→ 已發佈</option>
                                                            <option value="draft">→ 草稿</option>
                                                            <option value="archived">→ 封存</option>
                                                        </select>
                                                    </div>
                                                </td>

                                                <!-- 標籤 -->
                                                <td class="px-3 py-3">
                                                    <div class="flex flex-wrap gap-1">
                                                        <span v-for="tag in (doc.tags || [])" :key="tag.id"
                                                            class="text-xs px-1.5 py-0.5 rounded-full text-white"
                                                            :style="`background:${tag.color}`">
                                                            {{ tag.name }}
                                                        </span>
                                                        <button v-if="doc.is_owner !== false"
                                                            class="text-xs px-1 rounded"
                                                            style="color:var(--gray-400);border:1px dashed var(--border);"
                                                            @click="openTagEditModal(doc)"
                                                            title="編輯標籤">+</button>
                                                    </div>
                                                </td>

                                                <!-- 分享 -->
                                                <td class="px-3 py-3">
                                                    <span v-if="doc.is_shared_to_me"
                                                        class="text-xs px-2 py-0.5 rounded"
                                                        style="background:#fef9c3;color:#854d0e;">
                                                        📨 分享給我
                                                    </span>
                                                    <span v-else-if="doc.share_count > 0"
                                                        class="text-xs px-2 py-0.5 rounded cursor-pointer"
                                                        style="background:#dbeafe;color:#1e40af;"
                                                        @click="doc.is_owner !== false && openShareModal(doc)">
                                                        🔗 {{ doc.share_count }} 人
                                                    </span>
                                                    <span v-else class="text-xs" style="color:var(--gray-400);">—</span>
                                                </td>

                                                <!-- AI 摘要 -->
                                                <td class="px-3 py-3 hidden xl:table-cell text-xs" style="color:var(--gray-500);max-width:16rem;">
                                                    <span v-if="doc.ai_summary" class="line-clamp-2" :title="doc.ai_summary">{{ doc.ai_summary }}</span>
                                                    <span v-else-if="summarizingIds.has(doc.kb_id || doc.id)" style="color:var(--primary);">⟳ 生成中…</span>
                                                    <span v-else style="color:var(--gray-300);">—</span>
                                                </td>

                                                <!-- 操作 -->
                                                <td class="px-3 py-3 text-right" style="white-space:nowrap;">
                                                    <button class="btn btn-secondary text-xs mr-1" @click="openKbPreview(doc)" title="預覽">👁</button>
                                                    <button class="btn btn-secondary text-xs mr-1" @click="downloadKbFile(doc.id, doc.source_name)" title="下載">⬇</button>
                                                    <!-- 相關文件 -->
                                                    <button class="btn btn-secondary text-xs mr-1" @click="openRelatedModal(doc)" title="相關文件推薦">🔍</button>
                                                    <!-- 翻譯 -->
                                                    <button class="btn btn-secondary text-xs mr-1" @click="openTranslateModal(doc)" title="AI 翻譯摘要">🌐</button>
                                                    <!-- Diff -->
                                                    <button v-if="(doc.version||1) > 1" class="btn btn-secondary text-xs mr-1" @click="openDiffModal(doc)" title="版本差異比較">⟺</button>
                                                    <!-- 收藏 -->
                                                    <button class="btn text-xs mr-1"
                                                        :style="doc.is_favorited ? 'color:#f59e0b;' : 'color:var(--gray-400);'"
                                                        @click="doToggleFavorite(doc)"
                                                        :title="doc.is_favorited ? '取消收藏' : '加入收藏'">⭐</button>
                                                    <!-- 評分 -->
                                                    <button class="btn btn-secondary text-xs mr-1" @click="openRatingModal(doc)"
                                                        :title="`評分 (${doc.avg_rating || 0}★ / ${doc.rating_count || 0}則)`">
                                                        {{ doc.avg_rating ? `${doc.avg_rating}★` : '☆' }}
                                                    </button>
                                                    <!-- 訂閱 -->
                                                    <button class="btn text-xs mr-1"
                                                        :style="doc.is_subscribed ? 'color:var(--primary);' : 'color:var(--gray-400);'"
                                                        @click="doToggleSubscription(doc)"
                                                        :title="doc.is_subscribed ? '取消訂閱通知' : '訂閱更新通知'">🔔</button>
                                                    <!-- 送審 (非管理員 + 草稿) -->
                                                    <button v-if="doc.is_owner !== false && doc.status === 'draft'"
                                                        class="btn btn-secondary text-xs mr-1"
                                                        @click="doSubmitReview(doc)" title="送審">📤</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-secondary text-xs mr-1" @click="openShareModal(doc)" title="分享設定">🔗</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-secondary text-xs mr-1" @click="openVersionModal(doc)" title="版本歷史">📜</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-secondary text-xs mr-1"
                                                        :disabled="summarizingIds.has(doc.kb_id || doc.id)"
                                                        @click="doResummarize(doc)"
                                                        title="重新 AI 摘要">🔄</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-secondary text-xs mr-1" @click="openExpiryModal(doc)" title="設定到期日">📅</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-secondary text-xs mr-1" @click="openAccessLogs(doc)" title="存取記錄">📋</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-secondary text-xs mr-1"
                                                        @click="() => { uploadOptions.updateKbId = doc.kb_id || doc.id; $refs.fileInput.click() }"
                                                        title="上傳新版本">⬆</button>
                                                    <button v-if="doc.is_owner !== false" class="btn btn-danger text-xs"
                                                        :disabled="(ragState.deletingIds || []).includes(doc.id)"
                                                        @click="confirmDeleteDoc(doc)">
                                                        {{ (ragState.deletingIds || []).includes(doc.id) ? '…' : '刪除' }}
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- New Folder Modal -->
                        <div v-if="newFolderModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="newFolderModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-sm"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-4" style="color:var(--text);">新增資料夾</div>
                                <input v-model="newFolderModal.name"
                                    class="w-full border rounded p-2 text-sm mb-3"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);"
                                    placeholder="資料夾名稱"
                                    @keyup.enter="doCreateFolder"
                                    autofocus>
                                <div v-if="newFolderModal.error" class="text-xs mb-2" style="color:var(--danger);">{{ newFolderModal.error }}</div>
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="newFolderModal.visible=false">取消</button>
                                    <button class="btn btn-primary text-sm" :disabled="newFolderModal.saving || !newFolderModal.name.trim()" @click="doCreateFolder">
                                        {{ newFolderModal.saving ? '建立中...' : '建立' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Move Doc Modal -->
                        <div v-if="moveModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="moveModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-sm"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">移動文件到資料夾</div>
                                <div class="text-xs mb-4 truncate" style="color:var(--gray-500);">{{ moveModal.doc?.source_name }}</div>
                                <select v-model="moveModal.targetFolderId"
                                    class="w-full border rounded p-2 text-sm mb-4"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                    <option :value="null">📁 根目錄（不分類）</option>
                                    <option v-for="f in folderState.folders" :key="f.id" :value="f.id">
                                        📂 {{ f.name }}
                                    </option>
                                </select>
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="moveModal.visible=false">取消</button>
                                    <button class="btn btn-primary text-sm" :disabled="moveModal.saving" @click="doMoveDoc">
                                        {{ moveModal.saving ? '移動中...' : '確認移動' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Share Modal -->
                        <div v-if="shareModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="shareModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-lg"
                                style="background:var(--surface);max-height:85vh;overflow-y:auto;">
                                <div class="flex items-center justify-between mb-2">
                                    <div class="font-bold text-base" style="color:var(--text);">🔗 分享文件</div>
                                    <button class="text-lg" style="color:var(--gray-400);" @click="shareModal.visible=false">✕</button>
                                </div>
                                <div class="text-sm mb-3 truncate" style="color:var(--gray-500);">{{ shareModal.doc?.source_name }}</div>

                                <!-- 已分享名單 -->
                                <div class="mb-4">
                                    <div class="text-xs font-semibold mb-2" style="color:var(--gray-500);">已分享給：</div>
                                    <div v-if="!shareModal.shares.length" class="text-xs" style="color:var(--gray-400);">尚未分享給任何人</div>
                                    <div v-for="s in shareModal.shares" :key="s.user_id"
                                        class="flex items-center gap-2 py-1.5 px-2 rounded mb-1"
                                        style="background:var(--gray-50);border:1px solid var(--border);">
                                        <span class="text-sm flex-1" style="color:var(--text);">{{ s.email }}</span>
                                        <label class="flex items-center gap-1 text-xs cursor-pointer select-none" title="允許預覽">
                                            <input type="checkbox" :checked="s.can_preview"
                                                :disabled="shareModal.saving"
                                                @change="doTogglePerm(s, 'can_preview', $event.target.checked)">
                                            👁
                                        </label>
                                        <label class="flex items-center gap-1 text-xs cursor-pointer select-none" title="允許下載">
                                            <input type="checkbox" :checked="s.can_download"
                                                :disabled="shareModal.saving"
                                                @change="doTogglePerm(s, 'can_download', $event.target.checked)">
                                            ⬇
                                        </label>
                                        <button class="text-xs px-1" style="color:var(--danger);"
                                            :disabled="shareModal.saving"
                                            @click="doUnshare(s.user_id)">移除</button>
                                    </div>
                                </div>

                                <!-- 新增分享 Tabs -->
                                <div class="border-t pt-4" style="border-color:var(--border);">
                                    <div class="flex gap-1 mb-3">
                                        <button class="px-3 py-1 text-xs rounded"
                                            :style="shareTab==='user' ? 'background:var(--primary);color:#fff;' : 'background:var(--gray-100);color:var(--gray-600);'"
                                            @click="shareTab='user'">指定使用者</button>
                                        <button class="px-3 py-1 text-xs rounded"
                                            :style="shareTab==='dept' ? 'background:var(--primary);color:#fff;' : 'background:var(--gray-100);color:var(--gray-600);'"
                                            @click="shareTab='dept'">整個部門</button>
                                    </div>

                                    <!-- User tab -->
                                    <div v-if="shareTab==='user'">
                                        <select v-model="shareModal.selectedUserId"
                                            class="w-full border rounded p-2 text-sm mb-2"
                                            style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                            <option value="">— 選擇使用者 —</option>
                                            <option v-for="u in shareModal.availableUsers" :key="u.id" :value="u.id">
                                                {{ u.email }}
                                            </option>
                                        </select>
                                        <div class="flex items-center gap-4 mb-3 text-xs" style="color:var(--gray-600);">
                                            <label class="flex items-center gap-1 cursor-pointer">
                                                <input type="checkbox" v-model="shareModal.newCanPreview"> 👁 允許預覽
                                            </label>
                                            <label class="flex items-center gap-1 cursor-pointer">
                                                <input type="checkbox" v-model="shareModal.newCanDownload"> ⬇ 允許下載
                                            </label>
                                        </div>
                                        <button class="btn btn-primary text-sm w-full"
                                            :disabled="!shareModal.selectedUserId || shareModal.saving"
                                            @click="doShare()">
                                            {{ shareModal.saving ? '...' : '新增分享' }}
                                        </button>
                                    </div>

                                    <!-- Dept tab -->
                                    <div v-if="shareTab==='dept'">
                                        <select v-model="deptShareState.deptId"
                                            class="w-full border rounded p-2 text-sm mb-2"
                                            style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                            <option value="">— 選擇部門 —</option>
                                            <option v-for="d in (departments || [])" :key="d.id" :value="d.id">
                                                {{ d.name }}
                                            </option>
                                        </select>
                                        <div class="flex items-center gap-4 mb-3 text-xs" style="color:var(--gray-600);">
                                            <label class="flex items-center gap-1 cursor-pointer">
                                                <input type="checkbox" v-model="deptShareState.canPreview"> 👁 允許預覽
                                            </label>
                                            <label class="flex items-center gap-1 cursor-pointer">
                                                <input type="checkbox" v-model="deptShareState.canDownload"> ⬇ 允許下載
                                            </label>
                                        </div>
                                        <button class="btn btn-primary text-sm w-full"
                                            :disabled="!deptShareState.deptId || deptShareState.saving"
                                            @click="doDeptShare()">
                                            {{ deptShareState.saving ? '...' : '分享給部門全員' }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Tag Edit Modal -->
                        <div v-if="tagEditModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="tagEditModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-5 w-full max-w-sm" style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">🏷 編輯標籤</div>
                                <div class="text-xs mb-3 truncate" style="color:var(--gray-500);">{{ tagEditModal.doc?.source_name }}</div>
                                <div class="flex flex-wrap gap-2 mb-4">
                                    <button v-for="tag in tagState.tags" :key="tag.id"
                                        class="px-2 py-1 rounded-full text-xs border-2 transition-all"
                                        :style="tagEditModal.selected.has(tag.id)
                                            ? `background:${tag.color};color:#fff;border-color:${tag.color};`
                                            : `background:transparent;color:${tag.color};border-color:${tag.color};`"
                                        @click="toggleTagInEdit(tag.id)">
                                        {{ tag.name }}
                                    </button>
                                    <div v-if="!tagState.tags.length" class="text-xs" style="color:var(--gray-400);">尚無標籤，請先在側欄新增</div>
                                </div>
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="tagEditModal.visible=false">取消</button>
                                    <button class="btn btn-primary text-sm" :disabled="tagEditModal.saving" @click="saveDocTags">
                                        {{ tagEditModal.saving ? '儲存中...' : '儲存' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- New Tag Modal -->
                        <div v-if="newTagModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="newTagModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-5 w-full max-w-sm" style="background:var(--surface);">
                                <div class="font-bold text-base mb-4" style="color:var(--text);">新增標籤</div>
                                <input v-model="newTagModal.name" class="w-full border rounded p-2 text-sm mb-3"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);"
                                    placeholder="標籤名稱" @keyup.enter="doCreateTag">
                                <div class="flex flex-wrap gap-2 mb-3">
                                    <button v-for="c in TAG_COLORS" :key="c"
                                        class="w-6 h-6 rounded-full border-2 transition-all"
                                        :style="newTagModal.color===c ? `background:${c};border-color:#000;` : `background:${c};border-color:transparent;`"
                                        @click="newTagModal.color=c"></button>
                                </div>
                                <div v-if="newTagModal.error" class="text-xs mb-2" style="color:var(--danger);">{{ newTagModal.error }}</div>
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="newTagModal.visible=false">取消</button>
                                    <button class="btn btn-primary text-sm"
                                        :disabled="newTagModal.saving || !newTagModal.name.trim()"
                                        @click="doCreateTag">
                                        {{ newTagModal.saving ? '建立中...' : '建立' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Access Log Modal -->
                        <div v-if="accessLogModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="accessLogModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-5 w-full max-w-lg"
                                style="background:var(--surface);max-height:80vh;overflow-y:auto;">
                                <div class="flex items-center justify-between mb-2">
                                    <div class="font-bold text-base" style="color:var(--text);">📋 存取記錄</div>
                                    <button class="text-lg" style="color:var(--gray-400);" @click="accessLogModal.visible=false">✕</button>
                                </div>
                                <div class="text-xs mb-3 truncate" style="color:var(--gray-500);">{{ accessLogModal.doc?.source_name }}</div>
                                <div v-if="accessLogModal.loading" class="text-center p-8 text-sm" style="color:var(--gray-400);">載入中...</div>
                                <div v-else-if="!accessLogModal.logs.length" class="text-center p-8 text-sm" style="color:var(--gray-400);">尚無存取記錄</div>
                                <table v-else class="w-full text-sm">
                                    <thead>
                                        <tr style="background:var(--gray-50);border-bottom:1px solid var(--border);">
                                            <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">時間</th>
                                            <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">使用者</th>
                                            <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">動作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(log, i) in accessLogModal.logs" :key="i"
                                            class="border-b text-xs" style="border-color:var(--border);">
                                            <td class="px-3 py-2" style="color:var(--gray-500);">{{ log.accessed_at?.slice(0,19).replace('T',' ') }}</td>
                                            <td class="px-3 py-2" style="color:var(--text);">{{ log.email }}</td>
                                            <td class="px-3 py-2">
                                                <span class="px-2 py-0.5 rounded text-xs"
                                                    :style="log.action==='download'
                                                        ? 'background:#dbeafe;color:#1e40af;'
                                                        : 'background:#dcfce7;color:#166534;'">
                                                    {{ log.action === 'download' ? '⬇ 下載' : '👁 預覽' }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- Batch Move Modal -->
                        <div v-if="batchMoveModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="batchMoveModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-5 w-full max-w-sm" style="background:var(--surface);">
                                <div class="font-bold text-base mb-3" style="color:var(--text);">批次移動（{{ selectedDocIds.size }} 個文件）</div>
                                <select v-model="batchMoveModal.targetFolderId"
                                    class="w-full border rounded p-2 text-sm mb-4"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                    <option :value="null">📁 根目錄（不分類）</option>
                                    <option v-for="f in folderState.folders" :key="f.id" :value="f.id">📂 {{ f.name }}</option>
                                </select>
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="batchMoveModal.visible=false">取消</button>
                                    <button class="btn btn-primary text-sm" :disabled="batchMoveModal.saving" @click="doBatchMove">
                                        {{ batchMoveModal.saving ? '移動中...' : '確認移動' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Version History Modal -->
                        <div v-if="versionModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="versionModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">📜 版本歷史</div>
                                <div class="text-xs mb-4 truncate" style="color:var(--gray-500);">{{ versionModal.doc?.source_name }}</div>
                                <div v-if="versionModal.loading" class="text-sm text-center py-6" style="color:var(--gray-400);">載入中...</div>
                                <div v-else-if="!versionModal.versions.length" class="text-sm text-center py-6" style="color:var(--gray-400);">尚無歷史版本（此為第 1 版）</div>
                                <div v-else class="overflow-y-auto flex-1">
                                    <table class="w-full text-sm">
                                        <thead>
                                            <tr style="background:var(--gray-50);border-bottom:1px solid var(--border);">
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">版本</th>
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">時間</th>
                                                <th class="text-left px-3 py-2 font-medium" style="color:var(--gray-600);">備注</th>
                                                <th class="px-3 py-2"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="ver in versionModal.versions" :key="ver.id"
                                                class="border-b" style="border-color:var(--border);">
                                                <td class="px-3 py-2">
                                                    <span class="text-xs px-1.5 py-0.5 rounded" style="background:#e0e7ff;color:#4338ca;">v{{ ver.version }}</span>
                                                </td>
                                                <td class="px-3 py-2 text-xs" style="color:var(--gray-500);">
                                                    {{ ver.created_at?.slice(0,19).replace('T',' ') }}
                                                </td>
                                                <td class="px-3 py-2 text-xs" style="color:var(--gray-500);">{{ ver.note || '—' }}</td>
                                                <td class="px-3 py-2 text-right">
                                                    <button class="btn btn-secondary text-xs" @click="doRestoreVersion(ver)">還原此版</button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="flex justify-end mt-4">
                                    <button class="btn btn-secondary text-sm" @click="versionModal.visible=false">關閉</button>
                                </div>
                            </div>
                        </div>

                        <!-- Expiry Edit Modal -->
                        <div v-if="expiryModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="expiryModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-sm"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">📅 設定到期日</div>
                                <div class="text-xs mb-4 truncate" style="color:var(--gray-500);">{{ expiryModal.doc?.source_name }}</div>
                                <div class="mb-1 text-xs font-medium" style="color:var(--gray-600);">到期日（留空表示永不過期）</div>
                                <input type="date" v-model="expiryModal.expiresAt"
                                    class="w-full border rounded p-2 text-sm mb-4"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="expiryModal.visible=false">取消</button>
                                    <button class="btn btn-danger text-sm mr-auto" v-if="expiryModal.expiresAt"
                                        :disabled="expiryModal.saving"
                                        @click="expiryModal.expiresAt=''; saveExpiry()">清除到期日</button>
                                    <button class="btn btn-primary text-sm" :disabled="expiryModal.saving" @click="saveExpiry">
                                        {{ expiryModal.saving ? '儲存中...' : '儲存' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Related Docs Modal (17) -->
                        <div v-if="relatedModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="relatedModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[75vh] flex flex-col"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">🔍 相關文件推薦</div>
                                <div class="text-xs mb-4 truncate" style="color:var(--gray-500);">{{ relatedModal.doc?.source_name }}</div>
                                <div v-if="relatedModal.loading" class="text-sm text-center py-8" style="color:var(--gray-400);">AI 分析相似文件中...</div>
                                <div v-else-if="!relatedModal.items.length" class="text-sm text-center py-8" style="color:var(--gray-400);">找不到相關文件</div>
                                <div v-else class="overflow-y-auto flex-1 space-y-2">
                                    <div v-for="item in relatedModal.items" :key="item.doc_id"
                                        class="p-3 rounded-lg border cursor-pointer transition-colors hover:border-blue-300"
                                        style="border-color:var(--border);"
                                        @click="() => { relatedModal.visible=false; openKbPreview(item) }">
                                        <div class="font-medium text-sm" style="color:var(--text);">📄 {{ item.source_name || item.doc_id }}</div>
                                        <div v-if="item.snippet" class="text-xs mt-1 line-clamp-2" style="color:var(--gray-500);">{{ item.snippet }}</div>
                                    </div>
                                </div>
                                <div class="flex justify-end mt-4">
                                    <button class="btn btn-secondary text-sm" @click="relatedModal.visible=false">關閉</button>
                                </div>
                            </div>
                        </div>

                        <!-- Version Diff Modal (18) -->
                        <div v-if="diffModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="diffModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full flex flex-col"
                                style="background:var(--surface);max-width:72rem;height:88vh;">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">⟺ 版本差異比較</div>
                                <div class="text-xs mb-3 truncate" style="color:var(--gray-500);">{{ diffModal.doc?.source_name }}</div>
                                <div class="flex items-center gap-3 mb-4 flex-shrink-0">
                                    <span class="text-xs" style="color:var(--gray-600);">比較</span>
                                    <select v-model.number="diffModal.v1"
                                        class="border rounded px-2 py-1 text-xs"
                                        style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                        <option v-for="v in diffModal.doc?.version" :key="v" :value="v">v{{ v }}</option>
                                    </select>
                                    <span class="text-xs" style="color:var(--gray-600);">→</span>
                                    <select v-model.number="diffModal.v2"
                                        class="border rounded px-2 py-1 text-xs"
                                        style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                        <option v-for="v in diffModal.doc?.version" :key="v" :value="v">v{{ v }}</option>
                                    </select>
                                    <button class="btn btn-primary text-xs" :disabled="diffModal.loading" @click="loadDiff">
                                        {{ diffModal.loading ? '比較中...' : '開始比較' }}
                                    </button>
                                </div>
                                <div v-if="!diffModal.data && !diffModal.loading" class="flex-1 flex items-center justify-center text-sm" style="color:var(--gray-400);">選擇兩個版本後點擊「開始比較」</div>
                                <div v-else-if="diffModal.loading" class="flex-1 flex items-center justify-center text-sm" style="color:var(--gray-400);">分析差異中...</div>
                                <div v-else class="overflow-auto flex-1 text-xs">
                                    <div class="mb-2 text-xs" style="color:var(--gray-500);">
                                        v{{ diffModal.data.v1 }}：{{ diffModal.data.lines_a }} 行 　→　 v{{ diffModal.data.v2 }}：{{ diffModal.data.lines_b }} 行
                                    </div>
                                    <pre class="p-3 rounded text-xs overflow-x-auto"
                                        style="background:var(--gray-50);color:var(--text);white-space:pre-wrap;font-family:monospace;line-height:1.5;">{{ diffModal.data.unified }}</pre>
                                </div>
                                <div class="flex justify-end mt-3 flex-shrink-0">
                                    <button class="btn btn-secondary text-sm" @click="diffModal.visible=false">關閉</button>
                                </div>
                            </div>
                        </div>

                        <!-- Translate Modal (19) -->
                        <div v-if="translateModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="translateModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-lg"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">🌐 AI 多語言翻譯</div>
                                <div class="text-xs mb-1 truncate" style="color:var(--gray-500);">{{ translateModal.doc?.source_name }}</div>
                                <div class="text-xs mb-4 p-2 rounded" style="background:var(--gray-50);color:var(--gray-600);">
                                    原文摘要：{{ translateModal.doc?.ai_summary || '（無摘要，請先生成 AI 摘要）' }}
                                </div>
                                <div class="flex items-center gap-3 mb-4">
                                    <span class="text-xs font-medium" style="color:var(--gray-600);">目標語言：</span>
                                    <select v-model="translateModal.lang"
                                        class="border rounded px-2 py-1.5 text-sm flex-1"
                                        style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                        <option v-for="l in LANG_OPTIONS" :key="l.code" :value="l.code">{{ l.label }}</option>
                                    </select>
                                    <button class="btn btn-primary text-sm" :disabled="translateModal.loading" @click="doTranslate">
                                        {{ translateModal.loading ? '翻譯中...' : '翻譯' }}
                                    </button>
                                </div>
                                <div v-if="translateModal.result" class="p-3 rounded-lg text-sm"
                                    style="background:var(--gray-50);color:var(--text);min-height:4rem;white-space:pre-wrap;">
                                    {{ translateModal.result }}
                                </div>
                                <div v-else class="p-3 rounded-lg text-sm text-center" style="background:var(--gray-50);color:var(--gray-400);min-height:4rem;">
                                    選擇語言後點擊「翻譯」
                                </div>
                                <div class="flex justify-end mt-4">
                                    <button class="btn btn-secondary text-sm" @click="translateModal.visible=false">關閉</button>
                                </div>
                            </div>
                        </div>

                        <!-- Rating Modal -->
                        <div v-if="ratingModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="ratingModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">⭐ 文件評分與留言</div>
                                <div class="text-xs mb-4 truncate" style="color:var(--gray-500);">{{ ratingModal.doc?.source_name }}</div>
                                <!-- My rating -->
                                <div class="p-3 rounded-lg mb-4" style="background:var(--gray-50);border:1px solid var(--border);">
                                    <div class="text-xs font-medium mb-2" style="color:var(--gray-600);">我的評分</div>
                                    <div class="flex gap-1 mb-2">
                                        <button v-for="n in 5" :key="n"
                                            class="text-2xl leading-none transition-transform hover:scale-110"
                                            :style="n <= ratingModal.myScore ? 'color:#f59e0b;' : 'color:var(--gray-300);'"
                                            @click="ratingModal.myScore = n">★</button>
                                    </div>
                                    <textarea v-model="ratingModal.myComment" rows="2"
                                        class="w-full border rounded p-2 text-xs mb-2"
                                        style="background:var(--surface);border-color:var(--border);color:var(--text);"
                                        placeholder="留下你的使用心得（選填）..."></textarea>
                                    <button class="btn btn-primary text-xs" :disabled="ratingModal.saving" @click="saveRating">
                                        {{ ratingModal.saving ? '送出中...' : '送出評分' }}
                                    </button>
                                </div>
                                <!-- All ratings -->
                                <div v-if="ratingModal.loading" class="text-sm text-center py-4" style="color:var(--gray-400);">載入中...</div>
                                <div v-else class="overflow-y-auto flex-1">
                                    <div class="flex items-center gap-2 mb-3">
                                        <span class="text-2xl font-bold" style="color:#f59e0b;">{{ ratingModal.data?.avg || 0 }}</span>
                                        <span class="text-sm" style="color:#f59e0b;">{{ starStr(Math.round(ratingModal.data?.avg || 0)) }}</span>
                                        <span class="text-xs" style="color:var(--gray-400);">共 {{ ratingModal.data?.count || 0 }} 則評分</span>
                                    </div>
                                    <div v-for="r in ratingModal.data?.ratings" :key="r.id"
                                        class="border-b py-2" style="border-color:var(--border);">
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center gap-2">
                                                <span class="text-sm" style="color:#f59e0b;">{{ starStr(r.score) }}</span>
                                                <span class="text-xs" style="color:var(--gray-500);">{{ r.email }}</span>
                                            </div>
                                            <button v-if="r.is_mine || isAdminUser" class="text-xs" style="color:var(--danger);"
                                                @click="doDeleteRating(r)">刪除</button>
                                        </div>
                                        <div v-if="r.comment" class="text-xs mt-1" style="color:var(--gray-600);">{{ r.comment }}</div>
                                    </div>
                                    <div v-if="!ratingModal.data?.ratings?.length" class="text-xs text-center py-4" style="color:var(--gray-400);">尚無評分</div>
                                </div>
                                <div class="flex justify-end mt-4">
                                    <button class="btn btn-secondary text-sm" @click="ratingModal.visible=false">關閉</button>
                                </div>
                            </div>
                        </div>

                        <!-- Preview Modal -->
                        <div v-if="previewModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="previewModal.visible=false">
                            <div class="rounded-xl shadow-2xl flex flex-col w-full"
                                :style="previewModal.blobUrl ? 'max-width:56rem;height:90vh;' : 'max-width:44rem;max-height:85vh;'"
                                style="background:var(--surface);overflow:hidden;">
                                <div class="flex items-center justify-between px-5 py-3 border-b flex-shrink-0"
                                    style="border-color:var(--border);">
                                    <div>
                                        <div class="font-bold text-sm" style="color:var(--text);">📄 {{ previewModal.name }}</div>
                                        <div class="text-xs mt-0.5" style="color:var(--gray-400);">知識庫文件預覽（擷取文字）</div>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <label class="flex items-center gap-1 text-xs cursor-pointer select-none"
                                            style="color:var(--gray-500);" title="顯示/隱藏浮水印">
                                            <input type="checkbox" v-model="watermarkEnabled">
                                            💧 浮水印
                                        </label>
                                        <button class="btn btn-secondary text-xs"
                                            @click="downloadKbFile(previewModal.docId, previewModal.name)">
                                            ⬇ 下載原始檔
                                        </button>
                                        <button class="text-lg leading-none" style="color:var(--gray-400);"
                                            @click="previewModal.visible=false">✕</button>
                                    </div>
                                </div>
                                <div v-if="previewModal.loading" class="flex-1 flex items-center justify-center text-sm" style="color:var(--gray-400);">載入中...</div>
                                <div v-else-if="previewModal.error" class="flex-1 p-5 text-sm" style="color:var(--danger);">{{ previewModal.error }}</div>
                                <!-- PDF: embedded iframe + watermark overlay -->
                                <div v-else-if="previewModal.blobUrl" style="flex:1;position:relative;min-height:520px;">
                                    <iframe :src="previewModal.blobUrl"
                                        style="width:100%;height:100%;border:none;min-height:520px;"
                                        title="PDF 預覽"></iframe>
                                    <!-- Watermark overlay -->
                                    <div v-if="watermarkEnabled"
                                        style="position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:10;">
                                        <div v-for="row in 6" :key="row"
                                            style="display:flex;gap:80px;margin-bottom:90px;"
                                            :style="`margin-top:${(row-1)*90}px;`">
                                            <span v-for="col in 4" :key="col"
                                                style="
                                                    font-size:13px;font-weight:500;
                                                    color:rgba(100,100,100,0.18);
                                                    transform:rotate(-30deg);
                                                    white-space:nowrap;
                                                    display:inline-block;
                                                    user-select:none;
                                                ">{{ currentUser?.email || '機密文件' }}</span>
                                        </div>
                                    </div>
                                </div>
                                <!-- DOCX/PPTX/XLSX: no browser preview -->
                                <div v-else-if="!previewModal.content"
                                    class="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
                                    <div style="font-size:2.5rem;">📎</div>
                                    <div class="text-sm font-medium" style="color:var(--gray-700);">
                                        {{ previewModal.ext.toUpperCase() }} 格式無法在瀏覽器直接預覽
                                    </div>
                                    <div class="text-xs" style="color:var(--gray-400);">請下載後以對應軟體開啟（Office、WPS 等）</div>
                                    <button class="btn btn-primary text-sm mt-2"
                                        @click="downloadKbFile(previewModal.docId, previewModal.name)">
                                        ⬇ 立即下載
                                    </button>
                                </div>
                                <!-- Text / extracted content -->
                                <div v-else class="flex-1 overflow-y-auto p-5 text-sm whitespace-pre-wrap"
                                    style="color:var(--gray-800);line-height:1.7;font-family:monospace;">{{ previewModal.content }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Tab: 統計分析 -->
                    <div v-if="ragState.activeTab === 'analytics'">
                        <div v-if="analyticsLoading" class="p-8 text-center text-sm" style="color:var(--gray-400);">載入中...</div>
                        <div v-else-if="!analyticsData" class="p-8 text-center text-sm" style="color:var(--gray-400);">
                            <button class="btn btn-primary text-sm" @click="loadAnalytics">載入統計</button>
                        </div>
                        <div v-else>
                            <!-- KPI cards -->
                            <div class="grid grid-cols-3 gap-4 mb-6">
                                <div class="card p-4 text-center">
                                    <div class="text-3xl font-bold" style="color:var(--primary);">{{ analyticsData.total_docs }}</div>
                                    <div class="text-xs mt-1" style="color:var(--gray-500);">文件總數</div>
                                </div>
                                <div class="card p-4 text-center">
                                    <div class="text-3xl font-bold" style="color:#059669;">{{ analyticsData.total_views }}</div>
                                    <div class="text-xs mt-1" style="color:var(--gray-500);">總預覽次數</div>
                                </div>
                                <div class="card p-4 text-center">
                                    <div class="text-3xl font-bold" style="color:#7c3aed;">{{ analyticsData.total_downloads }}</div>
                                    <div class="text-xs mt-1" style="color:var(--gray-500);">總下載次數</div>
                                </div>
                            </div>
                            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <!-- Status distribution -->
                                <div class="card p-4">
                                    <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">文件狀態分佈</div>
                                    <div class="flex flex-wrap gap-2">
                                        <span v-for="s in analyticsData.status_dist" :key="s.status"
                                            class="flex items-center gap-1 text-sm px-3 py-1 rounded-full"
                                            :style="STATUS_STYLE[s.status] || STATUS_STYLE.draft">
                                            {{ STATUS_LABEL[s.status] || s.status }}
                                            <span class="font-bold">{{ s.count }}</span>
                                        </span>
                                    </div>
                                </div>
                                <!-- Tag distribution -->
                                <div class="card p-4">
                                    <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">熱門標籤</div>
                                    <div class="flex flex-wrap gap-2">
                                        <span v-for="t in analyticsData.tag_dist" :key="t.name"
                                            class="text-xs px-2 py-0.5 rounded-full text-white"
                                            :style="`background:${t.color || '#6b7280'}`">
                                            {{ t.name }} ({{ t.count }})
                                        </span>
                                    </div>
                                </div>
                                <!-- Top viewed -->
                                <div class="card p-4">
                                    <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">最多預覽</div>
                                    <div v-for="(item, i) in analyticsData.top_viewed" :key="item.id"
                                        class="flex items-center justify-between py-1.5 border-b text-sm"
                                        style="border-color:var(--border);">
                                        <span class="text-xs mr-2 font-bold w-5 text-center rounded"
                                            :style="i<3 ? 'color:#f59e0b;' : 'color:var(--gray-400);'">{{ i+1 }}</span>
                                        <span class="flex-1 truncate" style="color:var(--text);">{{ item.name }}</span>
                                        <span class="text-xs ml-2" style="color:var(--primary);">{{ item.count }} 次</span>
                                    </div>
                                    <div v-if="!analyticsData.top_viewed.length" class="text-xs text-center py-4" style="color:var(--gray-400);">尚無資料</div>
                                </div>
                                <!-- Top downloaded -->
                                <div class="card p-4">
                                    <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">最多下載</div>
                                    <div v-for="(item, i) in analyticsData.top_downloaded" :key="item.id"
                                        class="flex items-center justify-between py-1.5 border-b text-sm"
                                        style="border-color:var(--border);">
                                        <span class="text-xs mr-2 font-bold w-5 text-center"
                                            :style="i<3 ? 'color:#f59e0b;' : 'color:var(--gray-400);'">{{ i+1 }}</span>
                                        <span class="flex-1 truncate" style="color:var(--text);">{{ item.name }}</span>
                                        <span class="text-xs ml-2" style="color:#7c3aed;">{{ item.count }} 次</span>
                                    </div>
                                    <div v-if="!analyticsData.top_downloaded.length" class="text-xs text-center py-4" style="color:var(--gray-400);">尚無資料</div>
                                </div>
                                <!-- Daily activity -->
                                <div class="card p-4 lg:col-span-2">
                                    <div class="font-semibold text-sm mb-3" style="color:var(--gray-700);">近 30 天活動</div>
                                    <div v-if="!analyticsData.daily.length" class="text-xs text-center py-4" style="color:var(--gray-400);">尚無資料</div>
                                    <div v-else class="overflow-x-auto">
                                        <table class="w-full text-xs">
                                            <thead><tr style="color:var(--gray-500);">
                                                <th class="text-left py-1 pr-3">日期</th>
                                                <th class="text-right py-1 pr-3">預覽</th>
                                                <th class="text-right py-1">下載</th>
                                            </tr></thead>
                                            <tbody>
                                                <tr v-for="d in analyticsData.daily" :key="d.day"
                                                    class="border-t" style="border-color:var(--border);">
                                                    <td class="py-1 pr-3" style="color:var(--gray-500);">{{ d.day }}</td>
                                                    <td class="text-right py-1 pr-3" style="color:#059669;">{{ d.views }}</td>
                                                    <td class="text-right py-1" style="color:#7c3aed;">{{ d.downloads }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-4 text-right">
                                <button class="btn btn-secondary text-sm" @click="loadAnalytics">↻ 重新整理</button>
                            </div>
                        </div>
                    </div>

                    <!-- Tab: 審核佇列 -->
                    <div v-if="ragState.activeTab === 'review'">
                        <div class="flex items-center justify-between mb-4">
                            <div class="text-sm font-semibold" style="color:var(--gray-700);">
                                待審核文件（{{ reviewQueue.length }}）
                            </div>
                            <button class="btn btn-secondary text-sm" @click="loadReviewQueue" :disabled="reviewLoading">
                                <span v-if="reviewLoading" class="animate-spin">⟳</span> {{ reviewLoading ? '...' : '↻ 重新整理' }}
                            </button>
                        </div>
                        <div v-if="!reviewQueue.length" class="card p-8 text-center text-sm" style="color:var(--gray-400);">
                            目前沒有待審核的文件
                        </div>
                        <div v-else class="card overflow-x-auto">
                            <table class="w-full text-sm">
                                <thead>
                                    <tr style="background:var(--gray-50);border-bottom:1px solid var(--border);">
                                        <th class="text-left px-4 py-2 font-medium" style="color:var(--gray-600);">文件名稱</th>
                                        <th class="text-left px-4 py-2 font-medium" style="color:var(--gray-600);">送審者</th>
                                        <th class="text-left px-4 py-2 font-medium" style="color:var(--gray-600);">送審時間</th>
                                        <th class="text-right px-4 py-2 font-medium" style="color:var(--gray-600);">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in reviewQueue" :key="item.kb_id"
                                        class="border-b" style="border-color:var(--border);">
                                        <td class="px-4 py-3" style="color:var(--text);">{{ item.source_name }}</td>
                                        <td class="px-4 py-3 text-xs" style="color:var(--gray-500);">{{ item.requester_email || '—' }}</td>
                                        <td class="px-4 py-3 text-xs" style="color:var(--gray-500);">{{ item.submitted_at?.slice(0,19).replace('T',' ') || '—' }}</td>
                                        <td class="px-4 py-3 text-right">
                                            <button class="btn btn-secondary text-xs mr-1" @click="openKbPreview(item)" title="預覽">👁 預覽</button>
                                            <button class="btn btn-primary text-xs mr-1" @click="openReviewAction(item, 'approve')">✓ 核准</button>
                                            <button class="btn btn-danger text-xs" @click="openReviewAction(item, 'reject')">✕ 退回</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Review action modal -->
                        <div v-if="reviewModal.visible"
                            class="fixed inset-0 flex items-center justify-center z-50"
                            style="background:rgba(0,0,0,0.45);"
                            @click.self="reviewModal.visible=false">
                            <div class="rounded-xl shadow-2xl p-6 w-full max-w-sm"
                                style="background:var(--surface);">
                                <div class="font-bold text-base mb-1" style="color:var(--text);">
                                    {{ reviewModal.action === 'approve' ? '✓ 核准文件' : '✕ 退回文件' }}
                                </div>
                                <div class="text-xs mb-4 truncate" style="color:var(--gray-500);">{{ reviewModal.doc?.source_name }}</div>
                                <div class="mb-1 text-xs font-medium" style="color:var(--gray-600);">備注（選填）</div>
                                <textarea v-model="reviewModal.note" rows="3"
                                    class="w-full border rounded p-2 text-sm mb-4"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);"
                                    :placeholder="reviewModal.action === 'reject' ? '請說明退回原因...' : '審核備注（可留空）'">
                                </textarea>
                                <div class="flex gap-2 justify-end">
                                    <button class="btn btn-secondary text-sm" @click="reviewModal.visible=false">取消</button>
                                    <button :class="reviewModal.action === 'approve' ? 'btn btn-primary text-sm' : 'btn btn-danger text-sm'"
                                        :disabled="reviewModal.saving" @click="doReviewAction">
                                        {{ reviewModal.saving ? '處理中...' : reviewModal.action === 'approve' ? '確認核准' : '確認退回' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Tab: 知識圖譜 (20) -->
                    <div v-if="ragState.activeTab === 'graph'">
                        <div class="flex items-center justify-between mb-4">
                            <div class="text-sm font-semibold" style="color:var(--gray-700);">
                                知識圖譜（{{ graphData.nodes.length }} 個文件，{{ graphData.edges.length }} 條連線）
                            </div>
                            <div class="flex items-center gap-2">
                                <span class="text-xs" style="color:var(--gray-400);">節點以共同標籤連線</span>
                                <button class="btn btn-secondary text-sm" @click="computeGraphLayout">↻ 重新排列</button>
                            </div>
                        </div>
                        <div v-if="!graphData.nodes.length" class="card p-8 text-center text-sm" style="color:var(--gray-400);">
                            尚無文件資料，請先切換到「企業文件管理」頁籤載入。
                        </div>
                        <div v-else class="card overflow-hidden" style="height:540px;position:relative;">
                            <svg width="100%" height="100%" viewBox="0 0 700 500" style="display:block;">
                                <!-- Edges -->
                                <line v-for="(e, i) in graphData.edges" :key="'e'+i"
                                    :x1="graphPositions[e.source]?.x || 350"
                                    :y1="graphPositions[e.source]?.y || 250"
                                    :x2="graphPositions[e.target]?.x || 350"
                                    :y2="graphPositions[e.target]?.y || 250"
                                    :stroke-width="e.weight > 1 ? 2 : 1"
                                    stroke="var(--border)"
                                    :stroke-opacity="0.5 + e.weight * 0.1" />
                                <!-- Nodes -->
                                <g v-for="node in graphData.nodes" :key="node.id"
                                    style="cursor:pointer;"
                                    @mouseenter="graphHovered = node.id"
                                    @mouseleave="graphHovered = null"
                                    @click="() => { const doc = (ragState.knowledgeDocs||[]).find(d=>(d.kb_id||d.id)===node.id); if(doc) openKbPreview(doc) }">
                                    <circle
                                        :cx="graphPositions[node.id]?.x || 350"
                                        :cy="graphPositions[node.id]?.y || 250"
                                        :r="graphHovered === node.id ? 16 : 12"
                                        :fill="STATUS_STYLE[node.status]?.match(/background:([^;]+)/)?.[1] || '#e0e7ff'"
                                        stroke="var(--primary)"
                                        :stroke-width="graphHovered === node.id ? 2.5 : 1"
                                        style="transition:r 0.15s,stroke-width 0.15s;" />
                                    <text
                                        :x="graphPositions[node.id]?.x || 350"
                                        :y="(graphPositions[node.id]?.y || 250) + 26"
                                        text-anchor="middle"
                                        font-size="9"
                                        fill="var(--gray-600)"
                                        style="pointer-events:none;user-select:none;">
                                        {{ node.label }}
                                    </text>
                                    <text v-if="node.avgRating > 0"
                                        :x="(graphPositions[node.id]?.x || 350) + 10"
                                        :y="(graphPositions[node.id]?.y || 250) - 10"
                                        font-size="8" fill="#f59e0b"
                                        style="pointer-events:none;">★{{ node.avgRating }}</text>
                                </g>
                                <!-- Hover tooltip -->
                                <g v-if="graphHovered">
                                    <rect v-if="graphData.nodes.find(n=>n.id===graphHovered)"
                                        :x="(graphPositions[graphHovered]?.x || 350) + 18"
                                        :y="(graphPositions[graphHovered]?.y || 250) - 20"
                                        width="160" height="28" rx="4"
                                        fill="var(--surface)"
                                        stroke="var(--border)"
                                        stroke-width="1"
                                        style="filter:drop-shadow(0 2px 4px rgba(0,0,0,.1));" />
                                    <text v-if="graphData.nodes.find(n=>n.id===graphHovered)"
                                        :x="(graphPositions[graphHovered]?.x || 350) + 26"
                                        :y="(graphPositions[graphHovered]?.y || 250) - 2"
                                        font-size="9" fill="var(--text)"
                                        style="pointer-events:none;">
                                        {{ (ragState.knowledgeDocs||[]).find(d=>(d.kb_id||d.id)===graphHovered)?.source_name?.substring(0,24) || '' }}
                                    </text>
                                </g>
                            </svg>
                            <!-- Legend -->
                            <div class="absolute bottom-3 right-3 flex flex-col gap-1">
                                <div v-for="(style, key) in STATUS_STYLE" :key="key" class="flex items-center gap-1.5 text-xs">
                                    <span class="w-3 h-3 rounded-full flex-shrink-0" :style="`background:${style.match(/background:([^;]+)/)?.[1]}`"></span>
                                    <span style="color:var(--gray-500);">{{ STATUS_LABEL[key] }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="mt-2 text-xs text-center" style="color:var(--gray-400);">點擊節點可預覽文件；連線越粗代表共同標籤越多</div>
                    </div>

                    <!-- Tab: 郵件同步 -->
                    <div v-if="ragState.activeTab === 'sync'" class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="card p-6">
                            <h3 class="font-semibold mb-3" style="color:var(--gray-700);">同步伺服器郵件</h3>
                            <p class="text-sm mb-4" style="color:var(--gray-500);">將 Maildir 中的郵件批次匯入向量資料庫，讓 AI 能基於您的信箱歷史回答問題。</p>
                            <button @click="syncRagMaildir" class="btn btn-primary w-full" :disabled="isSyncing">
                                <span v-if="isSyncing" class="animate-spin mr-1">⟳</span>
                                {{ isSyncing ? '同步中...' : '立即同步 Maildir' }}
                            </button>
                            <p v-if="syncMessage" class="text-xs mt-2"
                                :style="syncMessage.includes('Error') || syncMessage.includes('失敗') ? 'color:var(--danger);' : 'color:var(--success);'">
                                {{ syncMessage }}
                            </p>
                        </div>
                        <div class="card p-6">
                            <h3 class="font-semibold mb-3" style="color:var(--gray-700);">手動匯入郵件</h3>
                            <div class="space-y-2">
                                <input v-model="ingestForm.subject" placeholder="主旨"
                                    class="w-full border p-2 rounded text-sm"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                <input v-model="ingestForm.sender" placeholder="寄件者"
                                    class="w-full border p-2 rounded text-sm"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);">
                                <textarea v-model="ingestForm.body" placeholder="郵件內容"
                                    rows="5" class="w-full border p-2 rounded text-sm"
                                    style="background:var(--surface);border-color:var(--border);color:var(--text);"></textarea>
                                <button @click="ingestEmail" class="btn btn-success w-full" :disabled="isIngesting">
                                    {{ isIngesting ? '匯入中...' : '匯入至知識庫' }}
                                </button>
                                <p v-if="ingestMessage" class="text-xs" style="color:var(--success);">{{ ingestMessage }}</p>
                            </div>
                        </div>
                    </div>
                </div>
</template>

<script setup>
import { ref, reactive, computed, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'
import { watch } from 'vue'
import axios from 'axios'

const ADMIN_API = import.meta.env.VITE_ADMIN_API || '/api'

async function _fetchDomainUsers() {
    const t = localStorage.getItem('token')
    const res = await axios.get(`${ADMIN_API}/crm/team`, {
        headers: { 'Authorization': `Bearer ${t}` }
    })
    return res.data || []
}

const store = useAppStore()

const stateRefs = storeToRefs(store)
const {
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  invoiceDriveState,
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  sslCerts, sslLoading, sslDomain,
  securityWizardStep, spamStats, securityFilterState,
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  ragState, ragStats, aiSalesState, aiMarketingState,
  kbAdminQueryInput, kbAdminLoading, kbAdminResult, kbAdminSources, kbAdminError,
  lineSupportState,
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  unrepliedEmails, unrepliedLoading,
  cbiInsights, cbiLoading,
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  ingestForm,
  chatMessages, isChatLoading, chatInput, isSyncing, syncMessage, isIngesting, ingestMessage,
} = stateRefs

const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchRagStats, fetchKbShares, addKbShare, removeKbShare, patchKbShare,
  fetchKbFolders, createKbFolder, deleteKbFolder, renameKbFolder, moveDocToFolder,
  fetchKbTags, createKbTag, deleteKbTag, setDocTags,
  fetchKbAccessLogs,
  batchDeleteKb, batchMoveKb,
  shareKbWithDept,
  fetchKbVersions, restoreKbVersion,
  setKbStatus, setKbExpiry, resummKbDoc, exportKbZip,
  fetchRelatedDocs, fetchKbDiff, translateKbDoc,
  toggleKbFavorite, fetchKbFavorites,
  submitKbRating, fetchKbRatings, deleteKbRating,
  fetchKbAnalytics,
  submitKbReview, fetchReviewQueue, approveKbDoc, rejectKbDoc,
  toggleKbSubscription, toggleFolderSubscription, fetchKbSubscriptions,
  downloadKbFile, fetchKbFileBlob,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  sendRagMessage, syncRagMaildir, ingestEmail,
} = store

const ragTabs = [
    { key: 'docs', label: '企業文件管理' },
    { key: 'chat', label: 'AI 問答' },
    { key: 'analytics', label: '統計分析' },
    { key: 'review', label: '審核佇列' },
    { key: 'graph', label: '知識圖譜' },
    { key: 'sync', label: '郵件同步' },
]

const watermarkEnabled = ref(true)

const isDragOver = ref(false)
const uploadFolderId = ref(null)

// Folder state
const folderState = reactive({ folders: [], loading: false, currentId: null })
const newFolderModal = reactive({ visible: false, name: '', saving: false, error: '' })
const moveModal = reactive({ visible: false, doc: null, targetFolderId: null, saving: false })


async function loadFolders() {
    folderState.loading = true
    try { folderState.folders = await fetchKbFolders() } catch (e) { folderState.folders = [] }
    finally { folderState.loading = false }
}

async function doCreateFolder() {
    if (!newFolderModal.name.trim()) return
    newFolderModal.saving = true
    newFolderModal.error = ''
    try {
        const f = await createKbFolder(newFolderModal.name.trim())
        folderState.folders.push(f)
        newFolderModal.name = ''
        newFolderModal.visible = false
    } catch (e) {
        newFolderModal.error = e.response?.data?.detail || e.message || '建立失敗'
    } finally {
        newFolderModal.saving = false
    }
}

async function confirmDeleteFolder(f) {
    const docCount = (ragState.value?.knowledgeDocs || []).filter(d => d.folder_id === f.id).length
    const msg = docCount > 0
        ? `確定要刪除資料夾「${f.name}」？其中 ${docCount} 個文件將移回根目錄，文件本身不會刪除。`
        : `確定要刪除資料夾「${f.name}」？`
    if (!confirm(msg)) return
    try {
        await deleteKbFolder(f.id)
        folderState.folders = folderState.folders.filter(x => x.id !== f.id)
        if (folderState.currentId === f.id) folderState.currentId = null
        await fetchRagDocuments()
    } catch (e) {
        alert('刪除失敗: ' + (e.response?.data?.detail || e.message))
    }
}

function openMoveModal(doc) {
    moveModal.doc = doc
    moveModal.targetFolderId = doc.folder_id ?? null
    moveModal.saving = false
    moveModal.visible = true
}

async function doMoveDoc() {
    if (!moveModal.doc) return
    moveModal.saving = true
    try {
        await moveDocToFolder(moveModal.doc.kb_id, moveModal.targetFolderId)
        moveModal.visible = false
        await fetchRagDocuments()
    } catch (e) {
        alert('移動失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        moveModal.saving = false
    }
}

function confirmDeleteDoc(doc) {
    const name = doc.source_name || doc.id
    if (!confirm(`確定要刪除文件「${name}」？此操作無法還原。`)) return
    deleteRagDocument(doc.id)
}

// Preview modal state
const previewModal = reactive({
    visible: false,
    docId: null,
    name: '',
    ext: '',
    loading: false,
    content: '',
    blobUrl: null,
    error: '',
})

function _closePrevPreviewBlob() {
    if (previewModal.blobUrl) {
        URL.revokeObjectURL(previewModal.blobUrl)
        previewModal.blobUrl = null
    }
}

const PREVIEW_AS_BLOB = new Set(['pdf'])
const PREVIEW_AS_TEXT = new Set(['txt', 'md', 'csv'])
const NO_BROWSER_PREVIEW = new Set(['docx', 'doc', 'pptx', 'ppt', 'xlsx', 'xls'])

async function openKbPreview(doc) {
    _closePrevPreviewBlob()
    const name = doc.source_name || doc.id
    const ext = (name.includes('.') ? name.split('.').pop() : '').toLowerCase()
    previewModal.visible = true
    previewModal.docId = doc.id
    previewModal.name = name
    previewModal.ext = ext
    previewModal.loading = true
    previewModal.content = ''
    previewModal.error = ''
    try {
        if (PREVIEW_AS_BLOB.has(ext)) {
            const { url } = await fetchKbFileBlob(doc.id)
            previewModal.blobUrl = url
        } else if (PREVIEW_AS_TEXT.has(ext) || !NO_BROWSER_PREVIEW.has(ext)) {
            const t = localStorage.getItem('token')
            const res = await axios.get(`${ADMIN_API}/rag/knowledge/${encodeURIComponent(doc.id)}/content`, {
                headers: { 'Authorization': `Bearer ${t}` }
            })
            previewModal.content = res.data?.content || '（無可預覽內容）'
        }
        // NO_BROWSER_PREVIEW: just show the hint, no fetch needed
    } catch (e) {
        previewModal.error = e.response?.data?.detail || e.message || '載入失敗'
    } finally {
        previewModal.loading = false
    }
}

watch(() => previewModal.visible, (v) => { if (!v) _closePrevPreviewBlob() })

// Share modal state
const shareModal = reactive({
    visible: false,
    doc: null,
    kbId: null,
    shares: [],
    availableUsers: [],
    selectedUserId: '',
    newCanPreview: true,
    newCanDownload: true,
    saving: false,
})

async function openShareModal(doc) {
    shareModal.doc = doc
    shareModal.kbId = doc.kb_id
    shareModal.visible = true
    shareModal.selectedUserId = ''
    shareModal.newCanPreview = true
    shareModal.newCanDownload = true
    shareModal.saving = false
    shareModal.shares = []
    shareModal.availableUsers = []
    const [sharesData, domainUsers] = await Promise.all([
        fetchKbShares(doc.kb_id),
        _fetchDomainUsers(),
    ])
    shareModal.shares = sharesData
    const sharedIds = new Set(sharesData.map(s => s.user_id))
    shareModal.availableUsers = domainUsers.filter(u =>
        u.id !== currentUser.value?.id && !sharedIds.has(u.id)
    )
}

async function doShare() {
    if (!shareModal.selectedUserId) return
    shareModal.saving = true
    try {
        await addKbShare(shareModal.kbId, [Number(shareModal.selectedUserId)],
            shareModal.newCanPreview, shareModal.newCanDownload)
        const [updated, domainUsers] = await Promise.all([
            fetchKbShares(shareModal.kbId),
            _fetchDomainUsers(),
        ])
        shareModal.shares = updated
        const sharedIds = new Set(updated.map(s => s.user_id))
        shareModal.availableUsers = domainUsers.filter(u =>
            u.id !== currentUser.value?.id && !sharedIds.has(u.id)
        )
        shareModal.selectedUserId = ''
        await fetchRagDocuments()
    } catch (e) {
        alert('分享失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        shareModal.saving = false
    }
}

async function doTogglePerm(share, field, value) {
    shareModal.saving = true
    try {
        await patchKbShare(shareModal.kbId, share.user_id, { [field]: value })
        share[field] = value
    } catch (e) {
        alert('更新失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        shareModal.saving = false
    }
}

async function doUnshare(userId) {
    shareModal.saving = true
    try {
        await removeKbShare(shareModal.kbId, userId)
        const [updated, domainUsers] = await Promise.all([
            fetchKbShares(shareModal.kbId),
            _fetchDomainUsers(),
        ])
        shareModal.shares = updated
        const sharedIds = new Set(updated.map(s => s.user_id))
        shareModal.availableUsers = domainUsers.filter(u =>
            u.id !== currentUser.value?.id && !sharedIds.has(u.id)
        )
        await fetchRagDocuments()
    } catch (e) {
        alert('取消分享失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        shareModal.saving = false
    }
}

// ── Tags ──────────────────────────────────────────────────────────
const tagState = reactive({ tags: [], loading: false })
const newTagModal = reactive({ visible: false, name: '', color: '#6366f1', saving: false, error: '' })
const tagFilterId = ref(null)  // null = no tag filter
const tagEditModal = reactive({ visible: false, doc: null, selected: new Set(), saving: false })

const TAG_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6']

async function loadTags() {
    tagState.loading = true
    try { tagState.tags = await fetchKbTags() } catch (e) { tagState.tags = [] }
    finally { tagState.loading = false }
}

async function doCreateTag() {
    if (!newTagModal.name.trim()) return
    newTagModal.saving = true
    newTagModal.error = ''
    try {
        const t = await createKbTag(newTagModal.name.trim(), newTagModal.color)
        tagState.tags.push(t)
        newTagModal.name = ''
        newTagModal.visible = false
    } catch (e) {
        newTagModal.error = e.response?.data?.detail || e.message || '建立失敗'
    } finally { newTagModal.saving = false }
}

async function confirmDeleteTag(tag) {
    if (!confirm(`確定刪除標籤「${tag.name}」？所有文件上的此標籤都會移除。`)) return
    try {
        await deleteKbTag(tag.id)
        tagState.tags = tagState.tags.filter(t => t.id !== tag.id)
        if (tagFilterId.value === tag.id) tagFilterId.value = null
        await fetchRagDocuments()
    } catch (e) {
        alert('刪除失敗: ' + (e.response?.data?.detail || e.message))
    }
}

function openTagEditModal(doc) {
    tagEditModal.doc = doc
    tagEditModal.selected = new Set((doc.tags || []).map(t => t.id))
    tagEditModal.saving = false
    tagEditModal.visible = true
}

function toggleTagInEdit(tagId) {
    if (tagEditModal.selected.has(tagId)) tagEditModal.selected.delete(tagId)
    else tagEditModal.selected.add(tagId)
}

async function saveDocTags() {
    if (!tagEditModal.doc) return
    tagEditModal.saving = true
    try {
        await setDocTags(tagEditModal.doc.kb_id, [...tagEditModal.selected])
        tagEditModal.visible = false
        await fetchRagDocuments()
    } catch (e) {
        alert('儲存失敗: ' + (e.response?.data?.detail || e.message))
    } finally { tagEditModal.saving = false }
}

// ── Batch operations ──────────────────────────────────────────────
const selectedDocIds = ref(new Set())  // Set of ChromaDB doc_id strings
const batchMoveModal = reactive({ visible: false, targetFolderId: null, saving: false })

const allDisplayedSelected = computed(() => {
    const docs = displayedDocs.value
    return docs.length > 0 && docs.every(d => selectedDocIds.value.has(d.id))
})

function toggleSelectDoc(docId) {
    const s = new Set(selectedDocIds.value)
    if (s.has(docId)) s.delete(docId)
    else s.add(docId)
    selectedDocIds.value = s
}

function toggleSelectAll() {
    if (allDisplayedSelected.value) {
        selectedDocIds.value = new Set()
    } else {
        selectedDocIds.value = new Set(displayedDocs.value.map(d => d.id))
    }
}

async function doBatchDelete() {
    const ids = [...selectedDocIds.value]
    if (!ids.length) return
    if (!confirm(`確定刪除已選取的 ${ids.length} 個文件？此操作無法還原。`)) return
    try {
        const res = await batchDeleteKb(ids)
        selectedDocIds.value = new Set()
        await fetchRagDocuments()
        if (res.errors?.length) alert(`${res.deleted.length} 個已刪除，${res.errors.length} 個失敗`)
    } catch (e) {
        alert('批次刪除失敗: ' + (e.response?.data?.detail || e.message))
    }
}

function openBatchMoveModal() {
    batchMoveModal.targetFolderId = null
    batchMoveModal.saving = false
    batchMoveModal.visible = true
}

async function doBatchMove() {
    const docs = displayedDocs.value.filter(d => selectedDocIds.value.has(d.id))
    const kbIds = docs.map(d => d.kb_id)
    batchMoveModal.saving = true
    try {
        await batchMoveKb(kbIds, batchMoveModal.targetFolderId)
        batchMoveModal.visible = false
        selectedDocIds.value = new Set()
        await fetchRagDocuments()
    } catch (e) {
        alert('批次移動失敗: ' + (e.response?.data?.detail || e.message))
    } finally { batchMoveModal.saving = false }
}

async function doBatchShareWithDept(deptId, canPreview, canDownload) {
    const docs = displayedDocs.value.filter(d => selectedDocIds.value.has(d.id) && d.is_owner !== false)
    let total = 0
    for (const doc of docs) {
        try {
            const r = await shareKbWithDept(doc.kb_id, deptId, canPreview, canDownload)
            total += r.added || 0
        } catch (e) {}
    }
    await fetchRagDocuments()
    alert(`已完成，共新增 ${total} 個分享`)
}

// ── Access Logs ───────────────────────────────────────────────────
const accessLogModal = reactive({ visible: false, doc: null, logs: [], loading: false })

async function openAccessLogs(doc) {
    accessLogModal.doc = doc
    accessLogModal.logs = []
    accessLogModal.loading = true
    accessLogModal.visible = true
    try { accessLogModal.logs = await fetchKbAccessLogs(doc.kb_id) }
    catch (e) { accessLogModal.logs = [] }
    finally { accessLogModal.loading = false }
}

// ── Share Modal — dept share tab ──────────────────────────────────
const shareTab = ref('user')   // 'user' | 'dept'
const deptShareState = reactive({ deptId: '', canPreview: true, canDownload: true, saving: false })

async function doDeptShare() {
    if (!deptShareState.deptId) return
    deptShareState.saving = true
    try {
        const r = await shareKbWithDept(shareModal.kbId, Number(deptShareState.deptId),
            deptShareState.canPreview, deptShareState.canDownload)
        deptShareState.deptId = ''
        // Refresh shares list
        shareModal.shares = await fetchKbShares(shareModal.kbId)
        await fetchRagDocuments()
        if (r.added === 0) alert('此部門成員已全部分享過了')
    } catch (e) {
        alert('部門分享失敗: ' + (e.response?.data?.detail || e.message))
    } finally { deptShareState.saving = false }
}

// ── Filter (folder + tag) ─────────────────────────────────────────
const isAdminUser = computed(() => {
    const role = currentUser.value?.role || ''
    return ['super_admin', 'domain_admin', 'admin'].includes(role)
})

const displayedDocs = computed(() => {
    let docs = ragState.value?.knowledgeDocs || []
    if (folderState.currentId !== null)
        docs = docs.filter(d => d.folder_id === folderState.currentId)
    if (tagFilterId.value !== null)
        docs = docs.filter(d => (d.tags || []).some(t => t.id === tagFilterId.value))
    if (showFavoritesOnly.value)
        docs = docs.filter(d => d.is_favorited)
    if (searchQ.value.trim())
        docs = docs  // server-side search already applied via fetchRagDocuments
    return docs
})

const searchQ = ref('')
let _searchTimer = null
function onSearchInput() {
    clearTimeout(_searchTimer)
    _searchTimer = setTimeout(() => fetchRagDocuments(searchQ.value.trim() || undefined), 400)
}

// ── Version Management ────────────────────────────────────────────
const versionModal = reactive({ visible: false, doc: null, versions: [], loading: false })

async function openVersionModal(doc) {
    versionModal.doc = doc
    versionModal.versions = []
    versionModal.loading = true
    versionModal.visible = true
    try { versionModal.versions = await fetchKbVersions(doc.kb_id) }
    catch (e) { versionModal.versions = [] }
    finally { versionModal.loading = false }
}

async function doRestoreVersion(ver) {
    if (!confirm(`確定還原到版本 v${ver.version}？目前的版本將被儲存為歷史記錄。`)) return
    try {
        await restoreKbVersion(versionModal.doc.kb_id, ver.id)
        versionModal.visible = false
        await fetchRagDocuments(searchQ.value.trim() || undefined)
    } catch (e) {
        alert('還原失敗: ' + (e.response?.data?.detail || e.message))
    }
}

// Upload-as-update modal
const updateVersionModal = reactive({ visible: false, doc: null })

// ── Status Management ─────────────────────────────────────────────
const STATUS_LABEL = { draft: '草稿', published: '已發佈', archived: '已封存', pending_review: '待審核' }
const STATUS_STYLE = {
    draft:          'background:#fef3c7;color:#92400e;',
    published:      'background:#d1fae5;color:#065f46;',
    archived:       'background:#f3f4f6;color:#6b7280;',
    pending_review: 'background:#ede9fe;color:#5b21b6;',
}

async function changeDocStatus(doc, newStatus) {
    const label = STATUS_LABEL[newStatus] || newStatus
    if (!confirm(`確定將「${doc.source_name || doc.id}」狀態改為「${label}」？`)) return
    try {
        await setKbStatus(doc.kb_id, newStatus)
        await fetchRagDocuments(searchQ.value.trim() || undefined)
    } catch (e) {
        alert('狀態變更失敗: ' + (e.response?.data?.detail || e.message))
    }
}

// ── Expiry Management ─────────────────────────────────────────────
const expiryModal = reactive({ visible: false, doc: null, expiresAt: '', saving: false })

function openExpiryModal(doc) {
    expiryModal.doc = doc
    expiryModal.expiresAt = doc.expires_at ? doc.expires_at.slice(0, 10) : ''
    expiryModal.saving = false
    expiryModal.visible = true
}

async function saveExpiry() {
    expiryModal.saving = true
    try {
        await setKbExpiry(expiryModal.doc.kb_id, expiryModal.expiresAt || null)
        expiryModal.visible = false
        await fetchRagDocuments(searchQ.value.trim() || undefined)
    } catch (e) {
        alert('儲存失敗: ' + (e.response?.data?.detail || e.message))
    } finally { expiryModal.saving = false }
}

function isExpiringSoon(expiresAt) {
    if (!expiresAt) return false
    const d = new Date(expiresAt)
    return d > new Date() && d < new Date(Date.now() + 7 * 86400000)
}

function isExpired(expiresAt) {
    if (!expiresAt) return false
    return new Date(expiresAt) < new Date()
}

// ── AI Summary ────────────────────────────────────────────────────
const summarizingIds = ref(new Set())

async function doResummarize(doc) {
    const s = new Set(summarizingIds.value)
    s.add(doc.kb_id)
    summarizingIds.value = s
    try {
        const res = await resummKbDoc(doc.kb_id)
        const found = (ragState.value?.knowledgeDocs || []).find(d => d.kb_id === doc.kb_id)
        if (found) found.ai_summary = res.ai_summary
    } catch (e) {
        alert('摘要失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        const s2 = new Set(summarizingIds.value); s2.delete(doc.kb_id); summarizingIds.value = s2
    }
}

// ── Upload with options ───────────────────────────────────────────
const uploadOptions = reactive({ status: 'published', expiresAt: '', updateKbId: null })

async function handleFileDrop(event) {
    isDragOver.value = false
    const files = Array.from(event.dataTransfer?.files || [])
    for (const file of files) {
        await uploadRagDocumentExt(file)
    }
}

async function handleFileSelect(event) {
    const files = Array.from(event.target?.files || [])
    for (const file of files) {
        await uploadRagDocumentExt(file)
    }
    event.target.value = ''
}

async function uploadRagDocumentExt(file) {
    const token = localStorage.getItem('token')
    ragState.value.uploadLoading = true
    ragState.value.uploadError = ''
    try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('title', file.name)
        formData.append('status', uploadOptions.status)
        if (uploadFolderId.value != null) formData.append('folder_id', String(uploadFolderId.value))
        if (uploadOptions.expiresAt) formData.append('expires_at', uploadOptions.expiresAt)
        if (uploadOptions.updateKbId != null) formData.append('update_kb_id', String(uploadOptions.updateKbId))
        const axios2 = (await import('axios')).default
        await axios2.post(`${ADMIN_API}/rag/upload_document`, formData, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        })
        await fetchRagDocuments(searchQ.value.trim() || undefined)
        await fetchRagStats()
    } catch (e) {
        ragState.value.uploadError = (e.response?.data?.detail || e.message || '上傳失敗').toString()
    } finally {
        ragState.value.uploadLoading = false
        uploadOptions.updateKbId = null
    }
}

// Load docs and folders when entering docs tab
watch(() => ragState.value?.activeTab, (tab) => {
    if (tab === 'docs') {
        fetchRagDocuments()
        loadFolders()
        loadTags()
        if (departments.value?.length === 0) fetchDepartments()
    }
    if (tab === 'analytics') loadAnalytics()
    if (tab === 'review') loadReviewQueue()
}, { immediate: true })

// ── Favorites ──────────────────────────────────────────────────────────────────
const showFavoritesOnly = ref(false)

async function doToggleFavorite(doc) {
    try {
        const r = await toggleKbFavorite(doc.kb_id)
        const found = (ragState.value?.knowledgeDocs || []).find(d => d.kb_id === doc.kb_id)
        if (found) found.is_favorited = r.favorited
    } catch (e) {
        alert('收藏失敗: ' + (e.response?.data?.detail || e.message))
    }
}

// ── Ratings ────────────────────────────────────────────────────────────────────
const ratingModal = reactive({ visible: false, doc: null, data: null, loading: false, myScore: 0, myComment: '', saving: false })

async function openRatingModal(doc) {
    ratingModal.doc = doc
    ratingModal.myScore = 0
    ratingModal.myComment = ''
    ratingModal.data = null
    ratingModal.loading = true
    ratingModal.visible = true
    try {
        ratingModal.data = await fetchKbRatings(doc.kb_id)
        const mine = ratingModal.data?.ratings?.find(r => r.is_mine)
        if (mine) { ratingModal.myScore = mine.score; ratingModal.myComment = mine.comment || '' }
    } finally { ratingModal.loading = false }
}

async function saveRating() {
    if (!ratingModal.myScore) { alert('請先選擇評分'); return }
    ratingModal.saving = true
    try {
        await submitKbRating(ratingModal.doc.kb_id, ratingModal.myScore, ratingModal.myComment)
        ratingModal.data = await fetchKbRatings(ratingModal.doc.kb_id)
        const found = (ragState.value?.knowledgeDocs || []).find(d => d.kb_id === ratingModal.doc.kb_id)
        if (found && ratingModal.data) { found.avg_rating = ratingModal.data.avg; found.rating_count = ratingModal.data.count }
    } catch (e) {
        alert('評分失敗: ' + (e.response?.data?.detail || e.message))
    } finally { ratingModal.saving = false }
}

async function doDeleteRating(rating) {
    if (!confirm('確定刪除此評論？')) return
    try {
        await deleteKbRating(ratingModal.doc.kb_id, rating.id)
        ratingModal.data = await fetchKbRatings(ratingModal.doc.kb_id)
    } catch (e) { alert('刪除失敗: ' + (e.response?.data?.detail || e.message)) }
}

// ── Analytics ──────────────────────────────────────────────────────────────────
const analyticsData = ref(null)
const analyticsLoading = ref(false)

async function loadAnalytics() {
    analyticsLoading.value = true
    try { analyticsData.value = await fetchKbAnalytics() }
    catch (e) { console.error(e) }
    finally { analyticsLoading.value = false }
}

// ── Review Workflow ────────────────────────────────────────────────────────────
const reviewQueue = ref([])
const reviewLoading = ref(false)
const reviewModal = reactive({ visible: false, doc: null, action: '', note: '', saving: false })

async function loadReviewQueue() {
    reviewLoading.value = true
    try { reviewQueue.value = await fetchReviewQueue() }
    catch (e) { reviewQueue.value = [] }
    finally { reviewLoading.value = false }
}

async function doSubmitReview(doc) {
    if (!confirm(`確定將「${doc.source_name}」送審？`)) return
    try {
        await submitKbReview(doc.kb_id)
        const found = (ragState.value?.knowledgeDocs || []).find(d => d.kb_id === doc.kb_id)
        if (found) found.status = 'pending_review'
    } catch (e) { alert('送審失敗: ' + (e.response?.data?.detail || e.message)) }
}

function openReviewAction(item, action) {
    reviewModal.doc = item
    reviewModal.action = action
    reviewModal.note = ''
    reviewModal.saving = false
    reviewModal.visible = true
}

async function doReviewAction() {
    reviewModal.saving = true
    try {
        if (reviewModal.action === 'approve') await approveKbDoc(reviewModal.doc.kb_id, reviewModal.note)
        else await rejectKbDoc(reviewModal.doc.kb_id, reviewModal.note)
        reviewModal.visible = false
        await loadReviewQueue()
        await fetchRagDocuments(searchQ.value.trim() || undefined)
    } catch (e) {
        alert('操作失敗: ' + (e.response?.data?.detail || e.message))
    } finally { reviewModal.saving = false }
}

// ── Subscriptions ──────────────────────────────────────────────────────────────
async function doToggleSubscription(doc) {
    try {
        const r = await toggleKbSubscription(doc.kb_id)
        const found = (ragState.value?.knowledgeDocs || []).find(d => d.kb_id === doc.kb_id)
        if (found) found.is_subscribed = r.subscribed
    } catch (e) { alert('訂閱操作失敗: ' + (e.response?.data?.detail || e.message)) }
}

async function doToggleFolderSub(folder) {
    try {
        await toggleFolderSubscription(folder.id)
    } catch (e) { alert('訂閱操作失敗: ' + (e.response?.data?.detail || e.message)) }
}

// helper: stars display
function starStr(score) {
    return '★'.repeat(score) + '☆'.repeat(5 - score)
}

// ── Related Docs (17) ──────────────────────────────────────────────────────────
const relatedModal = reactive({ visible: false, doc: null, items: [], loading: false })

async function openRelatedModal(doc) {
    relatedModal.doc = doc
    relatedModal.items = []
    relatedModal.loading = true
    relatedModal.visible = true
    try { relatedModal.items = await fetchRelatedDocs(doc.kb_id) }
    catch (e) { relatedModal.items = [] }
    finally { relatedModal.loading = false }
}

// ── Version Diff (18) ──────────────────────────────────────────────────────────
const diffModal = reactive({ visible: false, doc: null, v1: null, v2: null, data: null, loading: false })

async function openDiffModal(doc) {
    diffModal.doc = doc
    diffModal.data = null
    diffModal.loading = false
    diffModal.visible = true
    // default: compare v(current-1) with v(current)
    const cur = doc.version || 1
    diffModal.v2 = cur
    diffModal.v1 = cur > 1 ? cur - 1 : 1
}

async function loadDiff() {
    if (!diffModal.v1 || !diffModal.v2) return
    diffModal.loading = true
    diffModal.data = null
    try { diffModal.data = await fetchKbDiff(diffModal.doc.kb_id, diffModal.v1, diffModal.v2) }
    catch (e) { alert('Diff 失敗: ' + (e.response?.data?.detail || e.message)) }
    finally { diffModal.loading = false }
}

// ── Translation (19) ───────────────────────────────────────────────────────────
const translateModal = reactive({ visible: false, doc: null, lang: 'en', result: '', loading: false })
const LANG_OPTIONS = [
    { code: 'en', label: '英文 English' },
    { code: 'ja', label: '日文 日本語' },
    { code: 'ko', label: '韓文 한국어' },
    { code: 'es', label: '西班牙文 Español' },
]

async function openTranslateModal(doc) {
    translateModal.doc = doc
    translateModal.lang = 'en'
    translateModal.result = ''
    translateModal.loading = false
    translateModal.visible = true
}

async function doTranslate() {
    translateModal.loading = true
    translateModal.result = ''
    try {
        const r = await translateKbDoc(translateModal.doc.kb_id, translateModal.lang)
        translateModal.result = r.translated || '（無結果）'
    } catch (e) {
        alert('翻譯失敗: ' + (e.response?.data?.detail || e.message))
    } finally { translateModal.loading = false }
}

// ── Knowledge Graph (20) ───────────────────────────────────────────────────────
const graphInitialized = ref(false)

const graphData = computed(() => {
    const docs = ragState.value?.knowledgeDocs || []
    const nodes = docs.map(d => ({
        id: d.kb_id || d.id,
        label: (d.source_name || d.id || '').substring(0, 20),
        tags: (d.tags || []).map(t => t.id),
        status: d.status || 'published',
        avgRating: d.avg_rating || 0,
    }))
    const edges = []
    const seen = new Set()
    for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
            const shared = nodes[i].tags.filter(t => nodes[j].tags.includes(t))
            if (shared.length > 0) {
                const key = `${nodes[i].id}-${nodes[j].id}`
                if (!seen.has(key)) {
                    seen.add(key)
                    edges.push({ source: nodes[i].id, target: nodes[j].id, weight: shared.length })
                }
            }
        }
    }
    return { nodes, edges }
})

// Simple force layout (spring-repulsion, 50 iterations)
const graphPositions = ref({})

function computeGraphLayout() {
    const { nodes, edges } = graphData.value
    if (!nodes.length) return
    const W = 700, H = 500
    const pos = {}
    // Initialize positions in circle
    nodes.forEach((n, i) => {
        const angle = (2 * Math.PI * i) / nodes.length
        pos[n.id] = { x: W / 2 + (W / 2.5) * Math.cos(angle), y: H / 2 + (H / 2.5) * Math.sin(angle) }
    })
    const k = Math.sqrt((W * H) / Math.max(nodes.length, 1)) * 0.8
    for (let iter = 0; iter < 60; iter++) {
        const disp = {}
        nodes.forEach(n => { disp[n.id] = { x: 0, y: 0 } })
        // Repulsion
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const a = pos[nodes[i].id], b = pos[nodes[j].id]
                const dx = a.x - b.x, dy = a.y - b.y
                const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
                const force = (k * k) / dist
                disp[nodes[i].id].x += (dx / dist) * force
                disp[nodes[i].id].y += (dy / dist) * force
                disp[nodes[j].id].x -= (dx / dist) * force
                disp[nodes[j].id].y -= (dy / dist) * force
            }
        }
        // Attraction
        edges.forEach(e => {
            const a = pos[e.source], b = pos[e.target]
            if (!a || !b) return
            const dx = a.x - b.x, dy = a.y - b.y
            const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
            const force = (dist * dist) / k
            disp[e.source].x -= (dx / dist) * force
            disp[e.source].y -= (dy / dist) * force
            disp[e.target].x += (dx / dist) * force
            disp[e.target].y += (dy / dist) * force
        })
        // Apply
        const t = Math.max(20 - iter * 0.3, 1)
        nodes.forEach(n => {
            const d = disp[n.id]
            const len = Math.max(Math.sqrt(d.x * d.x + d.y * d.y), 1)
            pos[n.id].x = Math.max(40, Math.min(W - 40, pos[n.id].x + (d.x / len) * Math.min(len, t)))
            pos[n.id].y = Math.max(40, Math.min(H - 40, pos[n.id].y + (d.y / len) * Math.min(len, t)))
        })
    }
    graphPositions.value = pos
}

watch(() => ragState.value?.activeTab, (tab) => {
    if (tab === 'graph') {
        if (!graphInitialized.value) {
            fetchRagDocuments()
            graphInitialized.value = true
        }
        nextTick(() => computeGraphLayout())
    }
})

watch(graphData, () => {
    if (ragState.value?.activeTab === 'graph') computeGraphLayout()
}, { deep: true })

const graphHovered = ref(null)
</script>
